import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';

// Erros tipados traduzidos — o code é o contrato, nunca a string da mensagem.
const MENSAGEM_DE_ERRO = {
  PASSWORD_INVALID: 'Senha incorreta.',
  TWO_FACTOR_CODE_INVALID: 'Código inválido. Confira o app autenticador e tente novamente.',
  TWO_FACTOR_CODE_REUSED: 'Esse código já foi usado. Aguarde o próximo código do app.',
  TWO_FACTOR_ALREADY_ENABLED: 'A verificação em duas etapas já está ativa.',
  TWO_FACTOR_NOT_ENABLED: 'A verificação em duas etapas não está ativa.',
};

// Sem fallback pra error.response.data.message aqui de propósito: esse campo carrega o texto cru
// do backend (ex.: "rota não encontrada" de uma rota desligada pela feature flag), não uma
// mensagem pensada pra tela. Erro sem code mapeado mostra sempre o texto genérico da própria tela.
const mensagemDoErro = (error, padrao) => {
  const code = error.response?.data?.code;
  return MENSAGEM_DE_ERRO[code] || padrao;
};

const AccountSecurity = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const [carregando, setCarregando] = useState(true);
  const [erroDeCarga, setErroDeCarga] = useState('');
  const [ativo, setAtivo] = useState(false);

  // etapa: 'resumo' (desativado ou ativo) | 'senha-ativar' | 'qr' | 'recovery' | 'senha-desativar'
  // | 'senha-regenerar' | 'recovery-regenerado'
  const [etapa, setEtapa] = useState('resumo');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState('');
  const [processando, setProcessando] = useState(false);

  const [setupData, setSetupData] = useState(null); // { otpauthUri, secret }
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [confirmouGuardou, setConfirmouGuardou] = useState(false);

  const carregarStatus = async () => {
    setCarregando(true);
    setErroDeCarga('');
    try {
      const atualizado = await refreshUser();
      setAtivo(Boolean((atualizado || user)?.two_factor_enabled));
    } catch (err) {
      setErroDeCarga('Não foi possível carregar o status da verificação em duas etapas.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reiniciarWizard = () => {
    setEtapa('resumo');
    setSenha('');
    setCodigo('');
    setErro('');
    setSetupData(null);
    setQrDataUrl('');
    setRecoveryCodes([]);
    setConfirmouGuardou(false);
  };

  const irParaSenhaAtivar = () => {
    reiniciarWizard();
    setEtapa('senha-ativar');
  };

  const iniciarSetup = async (event) => {
    event.preventDefault();
    setErro('');
    setProcessando(true);
    try {
      const response = await api.post('/security/2fa/setup', { password: senha });
      const otpauthUri = response.data.otpauth_uri;
      setSetupData({ otpauthUri, secret: response.data.secret });
      setQrDataUrl(
        `https://quickchart.io/qr?text=${encodeURIComponent(otpauthUri)}&margin=1&size=240`
      );
      setCodigo('');
      setEtapa('qr');
    } catch (err) {
      setErro(mensagemDoErro(err, 'Não foi possível iniciar a ativação.'));
    } finally {
      setProcessando(false);
    }
  };

  const confirmarEnable = async (event) => {
    event.preventDefault();
    setErro('');
    setProcessando(true);
    try {
      const response = await api.post('/security/2fa/enable', { code: codigo });
      setRecoveryCodes(response.data.recovery_codes || []);
      setEtapa('recovery');
      await refreshUser();
      setAtivo(true);
    } catch (err) {
      setErro(mensagemDoErro(err, 'Não foi possível confirmar o código.'));
      setCodigo('');
    } finally {
      setProcessando(false);
    }
  };

  const finalizarAtivacao = () => {
    reiniciarWizard();
    setEtapa('resumo');
  };

  const irParaSenhaDesativar = () => {
    reiniciarWizard();
    setEtapa('senha-desativar');
  };

  const confirmarDisable = async (event) => {
    event.preventDefault();
    setErro('');
    setProcessando(true);
    try {
      await api.post('/security/2fa/disable', { password: senha, code: codigo });
      await refreshUser();
      setAtivo(false);
      reiniciarWizard();
    } catch (err) {
      setErro(mensagemDoErro(err, 'Não foi possível desativar.'));
      setCodigo('');
    } finally {
      setProcessando(false);
    }
  };

  const irParaSenhaRegenerar = () => {
    reiniciarWizard();
    setEtapa('senha-regenerar');
  };

  const confirmarRegenerate = async (event) => {
    event.preventDefault();
    setErro('');
    setProcessando(true);
    try {
      const response = await api.post('/security/2fa/recovery-codes/regenerate', {
        password: senha,
        code: codigo,
      });
      setRecoveryCodes(response.data.recovery_codes || []);
      setEtapa('recovery-regenerado');
    } catch (err) {
      setErro(mensagemDoErro(err, 'Não foi possível gerar novos códigos.'));
      setCodigo('');
    } finally {
      setProcessando(false);
    }
  };

  const copiarCodigos = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
    } catch (err) {
      // Sem clipboard disponível, o botão de baixar continua funcionando.
    }
  };

  const baixarCodigos = () => {
    const blob = new Blob([recoveryCodes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'stackfy-codigos-de-recuperacao.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderResumo = () => {
    if (ativo) {
      return (
        <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="flex items-center gap-3">
            <i className="bi-shield-check text-2xl text-emerald-600"></i>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Verificação em duas etapas ativa</h2>
              <p className="text-sm text-slate-500">
                O login pede o código do seu app autenticador além da senha.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={irParaSenhaRegenerar}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <i className="bi-arrow-repeat"></i> Gerar novos códigos de recuperação
            </button>
            <button
              type="button"
              onClick={irParaSenhaDesativar}
              className="inline-flex items-center gap-2 rounded-full border border-red-300 px-5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
            >
              <i className="bi-shield-x"></i> Desativar
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
        <div className="flex items-center gap-3">
          <i className="bi-shield text-2xl text-slate-400"></i>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Verificação em duas etapas desativada</h2>
            <p className="text-sm text-slate-500">
              Proteja sua conta com um código do seu app autenticador (Google Authenticator, Authy,
              1Password) além da senha.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={irParaSenhaAtivar}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-tBlue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
        >
          <i className="bi-shield-lock"></i> Ativar verificação em duas etapas
        </button>
      </div>
    );
  };

  const renderFormularioDeSenha = ({ titulo, descricao, aoSubmeter, textoDoBotao, comCodigo }) => (
    <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-10 sm:py-10 max-w-md mx-auto">
      <h2 className="text-base font-semibold text-slate-900">{titulo}</h2>
      <p className="mt-1 text-sm text-slate-500">{descricao}</p>

      <form onSubmit={aoSubmeter} className="mt-6 space-y-4">
        <div>
          <label htmlFor="senha-atual" className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Senha atual
          </label>
          <input
            id="senha-atual"
            type="password"
            className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 focus:border-tBlue focus:outline-none focus:ring-1 focus:ring-tBlue"
            value={senha}
            onChange={(event) => setSenha(event.target.value)}
            autoFocus
          />
        </div>

        {comCodigo ? (
          <div>
            <label htmlFor="codigo-do-app" className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Código do app autenticador
            </label>
            <input
              id="codigo-do-app"
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-center text-lg tracking-[0.4em] text-slate-700 focus:border-tBlue focus:outline-none focus:ring-1 focus:ring-tBlue"
              value={codigo}
              onChange={(event) => setCodigo(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
            />
          </div>
        ) : null}

        {erro ? (
          <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">{erro}</div>
        ) : null}

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={reiniciarWizard}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <i className="bi-arrow-left"></i> Cancelar
          </button>
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-full bg-tBlue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-tBlue2 disabled:opacity-60"
            disabled={processando || !senha || (comCodigo && codigo.length !== 6)}
          >
            {processando ? 'Confirmando...' : textoDoBotao}
          </button>
        </div>
      </form>
    </div>
  );

  const renderQr = () => (
    <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-10 sm:py-10 max-w-md mx-auto">
      <h2 className="text-base font-semibold text-slate-900">Escaneie o QR code</h2>
      <p className="mt-1 text-sm text-slate-500">
        Abra o app autenticador e escaneie o código abaixo. Se preferir, digite o segredo manualmente.
      </p>

      <div className="mt-6 flex justify-center">
        {qrDataUrl ? (
          <img src={qrDataUrl} alt="QR code para o app autenticador" className="h-56 w-56 rounded-2xl border border-slate-200" />
        ) : null}
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Ou digite este código</div>
        <div className="mt-1 font-mono text-sm tracking-wider text-slate-700 break-all">{setupData?.secret}</div>
      </div>

      <form onSubmit={confirmarEnable} className="mt-6 space-y-4">
        <div>
          <label htmlFor="codigo-de-confirmacao" className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Código de 6 dígitos gerado agora
          </label>
          <input
            id="codigo-de-confirmacao"
            type="text"
            inputMode="numeric"
            maxLength={6}
            autoFocus
            className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-center text-lg tracking-[0.4em] text-slate-700 focus:border-tBlue focus:outline-none focus:ring-1 focus:ring-tBlue"
            value={codigo}
            onChange={(event) => setCodigo(event.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
          />
        </div>

        {erro ? (
          <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">{erro}</div>
        ) : null}

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={reiniciarWizard}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <i className="bi-arrow-left"></i> Cancelar
          </button>
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-full bg-tBlue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-tBlue2 disabled:opacity-60"
            disabled={processando || codigo.length !== 6}
          >
            {processando ? 'Confirmando...' : 'Confirmar e ativar'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderRecoveryCodes = (regenerado) => (
    <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-10 sm:py-10 max-w-md mx-auto">
      <div className="flex items-center gap-3">
        <i className="bi-check-circle text-2xl text-emerald-600"></i>
        <h2 className="text-base font-semibold text-slate-900">
          {regenerado ? 'Novos códigos de recuperação' : 'Verificação em duas etapas ativada'}
        </h2>
      </div>

      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
        Estes códigos não aparecem de novo. Guarde em um lugar seguro — cada um serve para um único
        login, caso você perca o acesso ao app autenticador.
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm">
        {recoveryCodes.map((codigoRecuperacao) => (
          <div key={codigoRecuperacao} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-center">
            {codigoRecuperacao}
          </div>
        ))}
      </div>

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={copiarCodigos}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <i className="bi-clipboard"></i> Copiar
        </button>
        <button
          type="button"
          onClick={baixarCodigos}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <i className="bi-download"></i> Baixar .txt
        </button>
      </div>

      <label className="mt-6 flex items-start gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={confirmouGuardou}
          onChange={(event) => setConfirmouGuardou(event.target.checked)}
          className="mt-0.5"
        />
        Já guardei meus códigos de recuperação em um lugar seguro.
      </label>

      <button
        type="button"
        onClick={finalizarAtivacao}
        disabled={!confirmouGuardou}
        className="mt-4 w-full rounded-full bg-tBlue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-tBlue2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Concluir
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button type="button" className="text-2xl text-slate-700" onClick={toggleSidebar} aria-label="Abrir menu">
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>
            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="mx-auto max-w-3xl">
            <h1 className="text-xl font-semibold text-slate-900 mb-1">Segurança</h1>
            <p className="text-sm text-slate-500 mb-6">
              Verificação em duas etapas com um app autenticador (TOTP).
            </p>

            {carregando ? (
              <div className="rounded-3xl border border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500">
                Carregando...
              </div>
            ) : erroDeCarga ? (
              <div className="rounded-3xl border border-red-200 bg-red-50 px-6 py-10 text-center">
                <p className="text-sm text-red-600">{erroDeCarga}</p>
                <button
                  type="button"
                  onClick={carregarStatus}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-tBlue px-5 py-2 text-xs font-semibold text-white hover:bg-tBlue2 transition-colors"
                >
                  Tentar de novo
                </button>
              </div>
            ) : (
              <>
                {etapa === 'resumo' && renderResumo()}
                {etapa === 'senha-ativar' &&
                  renderFormularioDeSenha({
                    titulo: 'Confirme sua senha',
                    descricao: 'Para começar a ativação, confirme a senha da sua conta.',
                    aoSubmeter: iniciarSetup,
                    textoDoBotao: 'Continuar',
                    comCodigo: false,
                  })}
                {etapa === 'qr' && renderQr()}
                {etapa === 'recovery' && renderRecoveryCodes(false)}
                {etapa === 'senha-desativar' &&
                  renderFormularioDeSenha({
                    titulo: 'Desativar verificação em duas etapas',
                    descricao: 'Confirme a senha e um código do app autenticador para desativar.',
                    aoSubmeter: confirmarDisable,
                    textoDoBotao: 'Desativar',
                    comCodigo: true,
                  })}
                {etapa === 'senha-regenerar' &&
                  renderFormularioDeSenha({
                    titulo: 'Gerar novos códigos de recuperação',
                    descricao:
                      'Confirme a senha e um código do app autenticador. Os códigos antigos deixam de funcionar.',
                    aoSubmeter: confirmarRegenerate,
                    textoDoBotao: 'Gerar códigos novos',
                    comCodigo: true,
                  })}
                {etapa === 'recovery-regenerado' && renderRecoveryCodes(true)}
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default AccountSecurity;
